import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCheck, Settings } from "lucide-react";

export function NotificationBell({ compact = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => api.get("/notifications", { params: { limit: 30 } }).then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), 30000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const openItem = async (n) => {
    if (!n.read) await api.post("/notifications/read", { ids: [n.id] }).catch(() => {});
    setOpen(false); load(); navigate(n.os_id ? `/orders/${n.os_id}` : (n.link || "/notifications"));
  };
  const readAll = async () => { await api.post("/notifications/read", { all: true }); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={`relative rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 ${compact ? "p-2" : "p-2"}`} data-testid="notifications-bell" aria-label="Notificações">
          <Bell className="w-5 h-5" />
          {data.unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-unread-count">{data.unread > 99 ? "99+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align={compact ? "end" : "start"} className="w-[360px] max-w-[94vw] p-0 bg-white dark:bg-slate-900" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <p className="font-bold text-sm">Notificações {data.unread > 0 && <span className="text-slate-400 font-normal">· {data.unread} não lida(s)</span>}</p>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={readAll} disabled={!data.unread} data-testid="notifications-read-all"><CheckCheck className="w-3.5 h-3.5 mr-1" /> Ler todas</Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setOpen(false); navigate("/notifications"); }} data-testid="notifications-settings-btn" title="Preferências"><Settings className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {data.items.length === 0 && <p className="text-sm text-slate-500 text-center py-8" data-testid="notifications-empty">Nenhuma notificação por enquanto.</p>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/60 flex gap-3 ${n.read ? "" : "bg-emerald-500/5"}`} data-testid={`notification-item-${n.id}`}>
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-transparent border border-slate-300" : "bg-emerald-500"}`} />
              <span className="min-w-0">
                <span className={`block text-sm ${n.read ? "text-slate-700 dark:text-slate-300" : "font-semibold text-slate-900 dark:text-white"}`}>{n.title}</span>
                <span className="block text-xs text-slate-500 line-clamp-2">{n.body}</span>
                <span className="block text-[11px] text-slate-400 mt-0.5">{fmtDate(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
